import React from 'react';
import Icon from '@mdi/react';
import { GlobalVariables } from '../../../../pdf/GlobalVariables';
import { Button, MenuItem, TextField } from '@mui/material';
import { mdiPalette } from '@mdi/js';
import styles from './Compare.module.scss';
import i18next from 'i18next';
import {
    colorsToDropdownOptions,
    documentHistoryToDropdownOptions,
    concatClassNames,
    genRandomKey,
    toCSSColor,
    upperCaseBegin,
    devError,
    devLog,
} from '../../../../utils';
import type { DocumentApiHistory, IColorRGB, IDropdownOption } from '../../../../pdf/Interfaces';
import { ReduxUpdater } from '../../../../pdf/ReduxUpdater';
import api from '../../../../api';
import { backendApiUrl } from '../../../../App';
import { eplassSliceAnnotationSidepanelRefresh } from '../../../../Redux/EplassSlice';
import { useSelector } from 'react-redux';

export default () => {
    const forceRefresh = useSelector(eplassSliceAnnotationSidepanelRefresh);
    const [isProcessing, setIsProcessing] = React.useState<boolean>(false);
    const [selectedNoteID, setSelectedNoteID] = React.useState<string>(
        ReduxUpdater.documentNoteID2,
    );

    const renderColorProperty = (
        key: string,
        intColor: number | null,
        colors: IColorRGB[],
        icon: string,
    ) => {
        const title = i18next.t(`comparePanel.${key}`);
        const colorOptions: IDropdownOption[] = colorsToDropdownOptions(colors);
        const color = colorOptions.find(color => color.key === intColor);
        const colorName = color
            ? i18next.t(`colors.${color.text}`)
            : toCSSColor(intColor).toUpperCase();

        return (
            <TextField
                className={styles.dropDown}
                fullWidth
                disabled={true}
                label={title}
                value={colorName}
                margin="dense"
                size="small"
                InputProps={{
                    startAdornment: <Icon path={icon} style={{ color: toCSSColor(intColor) }} />,
                }}
            />
        );
    };

    const renderHistoryProperty = (
        key: string,
        history: string | null,
        options: DocumentApiHistory[],
        setNewValueFn: (newValue: string) => void,
    ) => {
        const title = i18next.t(`comparePanel.${key}`);
        const historyOptions: IDropdownOption[] = documentHistoryToDropdownOptions(options);
        // const showEditMode: boolean = historyOptions && historyOptions.length > 1;

        return (
            <TextField
                className={styles.dropDown}
                fullWidth
                select
                label={title}
                value={history}
                onChange={event => setNewValueFn(event.target.value as string)}
                margin="dense"
                size="small"
            >
                {/* {(!showEditMode
                    ? [{ key: intColor, text: toCSSColor(intColor).toUpperCase() }]
                    : historyOptions
                ) */}
                {historyOptions.map(history => (
                    <MenuItem className={styles.dropDown} key={history.key} value={history.key}>
                        {history.text}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const renderStringProperty = (key: string, value: string) => {
        const title = i18next.t(`comparePanel.${key}`);
        return (
            <TextField
                id={
                    GlobalVariables.isForTestRun
                        ? `annotPropsSidebarStrProp${upperCaseBegin(key)}`
                        : undefined
                }
                className={styles.fullWidth}
                key={`prop-${genRandomKey()}`}
                label={title}
                value={value}
                disabled={true}
                margin="dense"
                size="small"
                inputProps={{
                    style: {
                        WebkitTextFillColor: 'black',
                    },
                }}
            />
        );
    };

    const availableColors: IColorRGB[] = [
        {
            name: 'black',
            code: 0,
        },
        {
            name: 'black',
            code: 1,
        },
        {
            name: 'yellow',
            code: 16776960,
        },
        {
            name: 'red',
            code: 16711680,
        },
        {
            name: 'dark green',
            code: 32768,
        },
        {
            name: 'green',
            code: 65280,
        },
        {
            name: 'blue',
            code: 255,
        },
        {
            name: 'cyan',
            code: 65535,
        },
        {
            name: 'brown',
            code: 8388608,
        },
    ];

    const renderDynamicValues = React.useMemo(() => {
        if (ReduxUpdater.documentName2 !== '') {
            return (
                <>
                    {renderStringProperty('documentNumber', ReduxUpdater.documentName2)}
                    {renderHistoryProperty(
                        'indexVersion2',
                        selectedNoteID,
                        ReduxUpdater.documentHistory2.filter(
                            history => history.NoteId !== ReduxUpdater.documentNoteID,
                        ),
                        (newValue: string) => {
                            setSelectedNoteID(newValue);
                        },
                    )}
                </>
            );
        }

        return (
            <>
                {renderStringProperty('documentNumber', ReduxUpdater.documentName)}
                {renderHistoryProperty(
                    'indexVersion2',
                    selectedNoteID,
                    ReduxUpdater.documentHistory.filter(
                        history => history.NoteId !== ReduxUpdater.documentNoteID,
                    ),
                    (newValue: string) => {
                        setSelectedNoteID(newValue);
                    },
                )}
            </>
        );
    }, [forceRefresh, selectedNoteID]);

    return (
        <div className={concatClassNames(styles.annotPropsTable)}>
            <h3
                style={{
                    margin: '0px',
                }}
            >
                {i18next.t('comparePanel.currentVersion')}
            </h3>
            {renderStringProperty('documentNumber', ReduxUpdater.documentName)}
            {renderStringProperty('indexVersion', ReduxUpdater.documentVersion)}
            {renderColorProperty(
                'color',
                ReduxUpdater.compareColorRemoved,
                availableColors,
                mdiPalette,
            )}
            <br />
            <br />
            <h3
                style={{
                    margin: '0px',
                }}
            >
                {i18next.t('comparePanel.comparedVersion')}
            </h3>
            {renderDynamicValues}
            {renderColorProperty(
                'color2',
                ReduxUpdater.compareColorAdded,
                availableColors,
                mdiPalette,
            )}
            <br />
            <Button
                className={styles.fullWidth}
                disabled={!selectedNoteID || isProcessing}
                variant="contained"
                sx={{ color: 'white', backgroundColor: '#44AC32' }}
                onClick={() => {
                    setIsProcessing(true);

                    const selectedDocumentHistory = ReduxUpdater.documentHistory.find(
                        history => history.NoteId === selectedNoteID,
                    );

                    if (!selectedDocumentHistory) {
                        setIsProcessing(false);
                        return;
                    }

                    api.get<DocumentApiHistory[]>(backendApiUrl.document.register, {
                        headers: {
                            viewerKey: ReduxUpdater.documentUrl,
                            universalId: selectedDocumentHistory.UniversalID,
                            idDocIndex: selectedDocumentHistory.IdDocIndex,
                            fileName: selectedDocumentHistory.Files.FileName,
                            docTitle: selectedDocumentHistory.subject,
                        },
                    })
                        .then(
                            viewerKey => {
                                devLog(viewerKey);

                                window.open(
                                    `${window.location.origin}/${ReduxUpdater.documentUrl}/${viewerKey}?new=${ReduxUpdater.compareColorAdded.toString(16).padStart(6, '0')}&old=${ReduxUpdater.compareColorRemoved.toString(16).padStart(6, '0')}`,
                                    '_blank',
                                );
                                setIsProcessing(false);
                            },
                            error => {
                                devError(error);
                                setIsProcessing(false);
                            },
                        )
                        .catch(error => {
                            devError(error);
                            setIsProcessing(false);
                        });
                }}
            >
                {i18next.t('comparePanel.buttonText')}
            </Button>
            <br />
            <p
                style={{
                    border: '2px solid red',
                    color: 'red',
                    fontSize: '12px',
                    padding: '5px',
                }}
            >
                {i18next.t('comparePanel.note')}
            </p>
        </div>
    );
};
